import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Animated, Easing, View } from 'react-native'

import { format, parseISO } from 'date-fns'
import { isEmpty } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { getClientPreferredNameIfExists } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { HeaderText } from '../../atoms/headerText/HeaderText'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { ShieldTriageIcon } from '../../atoms/icons/ShieldTriageIcon'
import { TextButton } from '../../atoms/textButton/TextButton'
import { Size as HeaderTextSize } from '../../atoms/title/Title'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { SidePanelToggle } from '../../molecules/sidePanel/SidePanelToggle'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const HeaderContainer = styled.View<{
  theme: ThemeType
  height: number
}>(({ theme: { colors, spacing }, height }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  position: 'sticky',
  top: '0px',
  zIndex: 1,
  height,
  backgroundColor: colors.backgroundPrimary,
  paddingLeft: spacing['24px'],
  paddingRight: spacing['24px'],
  borderBottomWidth: '1px',
  borderStyle: 'solid',
  borderBottomColor: colors.borderDefault,
  alignItems: 'center',
  width: '100%',
}))

const LeftInnerContainer = styled(Animated.View)<{ theme: ThemeType }>(() => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
}))

const DateContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  paddingTop: spacing['8px'],
}))

const ButtonContainer = styled.View<{ theme: ThemeType }>({
  flexDirection: 'row',
})

const ExitButton = styled(TextButton)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  alignSelf: 'center',
  marginRight: spacing['24px'],
}))

const LoadingIndicatorContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginRight: spacing['8px'],
}))

const hipaaElementStyle = { marginLeft: 4 }

export type ClientDetailsHeaderProps = {
  clientDetails: ClientObject | null
  handleExit: () => void
  noteIsBeingAutoSaved: boolean
  noteStatus: NoteStatusStates
  noteType: NoteTypes
  sessionNumber: string
  showSubmit: boolean
  submitButton: () => React.JSX.Element
  submitDate: string | null
  updateDate: string
  hasSidePanelToggle: boolean
  height?: number
  setIsTabbedExpandingSidePanelOpen: (isOpen: boolean) => void
  isSideBarIconVisible?: boolean
  // Optional overrides to reuse this header for other flows (e.g., Close Session)
  headerTextOverride?: string
  secondaryTextOverride?: string | null
  exitButtonTextOverride?: string
  showHipaaSecuredBadge?: boolean
}

export const ClientDetailsHeader: FunctionComponent<ClientDetailsHeaderProps> = ({
  clientDetails,
  handleExit,
  noteIsBeingAutoSaved,
  noteStatus,
  noteType,
  sessionNumber,
  showSubmit,
  submitButton,
  submitDate,
  updateDate,
  hasSidePanelToggle,
  height = 114,
  setIsTabbedExpandingSidePanelOpen,
  isSideBarIconVisible,
  headerTextOverride,
  secondaryTextOverride,
  exitButtonTextOverride,
  showHipaaSecuredBadge = false,
}) => {
  const { formatMessage } = useIntl()
  const { colors, spacing } = useTheme()
  const clientFullName = getClientPreferredNameIfExists(clientDetails) || clientDetails?.full_name

  // Compress/expand the right-side area based on isSideBarIconVisible
  const sideIconVisible = isSideBarIconVisible ?? true

  // Tokens for animated margins so we match theme spacing
  const MARGIN_SMALL = useMemo(() => parseInt(spacing['24px'], 10), [spacing])
  const MARGIN_LARGE = useMemo(() => parseInt(spacing['32px'], 10), [spacing])
  // Animate left container marginRight so it doesn't snap
  const marginRightAnim = useRef(
    new Animated.Value(hasSidePanelToggle ? (!sideIconVisible ? MARGIN_SMALL : MARGIN_LARGE) : 0),
  ).current
  useEffect(() => {
    const to = hasSidePanelToggle ? (!sideIconVisible ? MARGIN_SMALL : MARGIN_LARGE) : 0
    Animated.timing(marginRightAnim, {
      toValue: to,
      duration: 50,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: false,
    }).start()
  }, [hasSidePanelToggle, sideIconVisible, MARGIN_SMALL, MARGIN_LARGE, marginRightAnim])

  const noteHeaderText = useCallback(() => {
    switch (noteType) {
      case NoteTypes.GENERAL: {
        return formatMessage(
          {
            defaultMessage: 'General note for {fullName}',
            description: 'Header title for a General Note',
          },
          { fullName: clientFullName },
        )
      }
      case NoteTypes.INITIAL:
      case NoteTypes.SESSION: {
        return formatMessage(
          {
            defaultMessage: 'Session {sessionNumber} note for {fullName}',
            description: 'Header title for a Session Note',
          },
          { fullName: clientFullName, sessionNumber },
        )
      }
      case NoteTypes.REFERRAL: {
        return formatMessage(
          {
            defaultMessage: 'Care referral form for {fullName}',
            description: 'Header title for a Session Note',
          },
          { fullName: clientFullName },
        )
      }
      case NoteTypes.DISCHARGE: {
        return formatMessage(
          {
            defaultMessage: 'Discharge note for {fullName}',
            description: 'Header title for a Discharge Note',
          },
          { fullName: clientFullName },
        )
      }
      default: {
        return ''
      }
    }
  }, [clientFullName, formatMessage, noteType, sessionNumber])

  const [readOnly, computedDate] = useMemo(() => {
    switch (noteStatus) {
      case NoteStatusStates.COMPLETED:
        return [
          true,
          submitDate
            ? `Submitted on ${format(parseISO(submitDate), 'MM/d/yy')} at ${format(parseISO(submitDate), 'hh:mm a')}`
            : '',
        ]
      case NoteStatusStates.DRAFT:
        return [
          false,
          `Saved on ${format(parseISO(updateDate), 'MM/d/yy')} at ${format(parseISO(updateDate), 'hh:mm a')}`,
        ]
      default:
        return [false, null]
    }
  }, [noteStatus, submitDate, updateDate])

  const finalDate = secondaryTextOverride ?? computedDate

  const renderDateContainer = () => {
    return (
      <DateContainer testID={tID('NoteDetailsHeader-date-container')}>
        {!isEmpty(finalDate) && (
          <>
            {!secondaryTextOverride && noteIsBeingAutoSaved && (
              <LoadingIndicatorContainer>
                <LoadingIndicator size={24} topPadding={0} />
              </LoadingIndicatorContainer>
            )}
            <BodyText
              size={BodyTextSize.SMALL}
              color={colors.textSecondary}
              text={
                readOnly
                  ? finalDate
                  : !secondaryTextOverride && noteIsBeingAutoSaved
                    ? formatMessage({
                        defaultMessage: 'Saving note',
                        description: 'Label indicating that the note is being saved',
                      })
                    : finalDate
              }
            />
          </>
        )}
        {showHipaaSecuredBadge && (
          <>
            {!isEmpty(finalDate) && (
              <BodyText
                size={BodyTextSize.SMALL}
                color={colors.textSecondary}
                style={hipaaElementStyle}
                text={formatMessage({
                  defaultMessage: '\u00B7',
                  description: 'Middle dot separator between date and HIPAA badge',
                })}
              />
            )}
            <BodyText
              size={BodyTextSize.SMALL}
              color={colors.textSecondary}
              style={isEmpty(finalDate) ? undefined : hipaaElementStyle}
              text={formatMessage({
                defaultMessage: 'HIPAA secured',
                description: 'Label indicating notes are HIPAA secured',
              })}
            />
            <Tooltip
              content={formatMessage({
                defaultMessage: 'Your notes are protected in accordance with HIPAA and applicable security standards.',
                description: 'Tooltip explaining HIPAA and security standards for notes',
              })}
              placement='bottom'
              hoverEnabled
              testID={tID('HipaaSecuredTooltip')}
              wrapperStyle={hipaaElementStyle}
            >
              <ShieldTriageIcon size={16} color={colors.textSecondary} />
            </Tooltip>
          </>
        )}
      </DateContainer>
    )
  }

  const exitButtonText =
    exitButtonTextOverride ??
    formatMessage({
      defaultMessage: 'Exit',
      description: 'Button that exits filling out the Note',
    })

  return (
    <HeaderContainer height={height} testID={tID('NoteDetailsHeader')}>
      <LeftInnerContainer style={{ marginRight: marginRightAnim }}>
        <View>
          <HeaderText text={headerTextOverride ?? noteHeaderText()} size={HeaderTextSize.LARGE} />
          {renderDateContainer()}
        </View>
        <ButtonContainer>
          {noteStatus === NoteStatusStates.COMPLETED || !showSubmit ? (
            <TextButton
              text={formatMessage({
                defaultMessage: 'Close',
                description: 'Button that closes the Note modal',
              })}
              onPress={handleExit}
              testID={tID('ClientDetails-close-button')}
            />
          ) : (
            <>
              <ExitButton text={exitButtonText} onPress={handleExit} testID={tID('ClientDetails-exit-button')} />
              {submitButton()}
            </>
          )}
        </ButtonContainer>
      </LeftInnerContainer>
      {hasSidePanelToggle && (
        <SidePanelToggle
          visible={sideIconVisible}
          onOpen={() => setIsTabbedExpandingSidePanelOpen(true)}
          testID={tID('Notes-sidePanel-button')}
        />
      )}
    </HeaderContainer>
  )
}
